import { AddFilterBaseFunc, FilterContext, IHierarchyPlotData } from "models/core";

//default colors can be found at https://plotly.com/javascript/reference/layout/#layout-colorway
const UNKNOWN_COLOR = "#bbd854"; //light green

export const graphColors = {
    unknown: UNKNOWN_COLOR,
    default: ["#1e88e5", "#f79325", "#ffd92f", UNKNOWN_COLOR], //blue, orange, yellow
}

const DEFAULT_CHART_HEIGHT = 260;
const DEMOGRAPHIC_CHART_HEIGHT = 260;
const TALL_CHART_HEIGHT = 460;
const HORIZONTAL_BAR_MARGIN = 0;

export type ChartEventHandler = (e: { points: Plotly.PlotDatum[]; }) => void
export type LegendFilter = (value: string) => void;

/**
 * Default layout for most charts
 */
export const defaultLayout: Partial<Plotly.Layout> = {
    font: { size: 10 },
    margin: {
        l: 80,
        r: 50,
        b: 50,
        t: 10,
        pad: 1
    },
    xaxis: { title: { font: { size: 10 } } },
    yaxis: { title: { font: { size: 10 } } },
    dragmode: "select" as any,
    colorway: graphColors.default,
};

/**
 * Default chart configuration
 */
export const defaultConfig: Partial<Plotly.Config> = {
    displaylogo: false,
    displayModeBar: false,
    responsive: false
};

/**
 * Common box styling
 * @param height
 */
export function boxStyle(height: number) {
    return {
        minHeight: height,
        maxHeight: height,
        height: height,
        padding: 1,
    };
}

/**
 * Default shared styles
 */
export const defaultStyles = {
    demographicBox: boxStyle(DEMOGRAPHIC_CHART_HEIGHT),
    box: boxStyle,
    defaultBox: boxStyle(DEFAULT_CHART_HEIGHT),
    tallBox: boxStyle(TALL_CHART_HEIGHT),
    inheritHW: {
        width: "inherit",
        height: "inherit"
    }
};

/**
 * Common calculations for attributes for horizontal bar charts
 * 
 * @param values Plot values
 * @param height Expected / max height
 * @param maxMultiplier Used to extend the axes of the chart
 * @param allowFilter Allow filtering / click events
 * @param barHeight Height to render the bar traces
 */
export function horizontalBarchartAttrs(values: Array<number>, height: number = 480, maxMultiplier: number = 2, allowFilter: boolean = true, barHeight: number = 32) {
    //Determine the height of the chart so it can be wrapped in a scrollable container
    const array = values || [];
    const count = array.length;

    let maxHeight = height - 50;
    const barsHeight = count === 0
        ? maxHeight
        : count * (barHeight + HORIZONTAL_BAR_MARGIN);

    //If the chart shorter than the expected height centre it vertically
    const verticalAlign = (maxHeight - barsHeight > 0) ? (maxHeight - barsHeight) / 2 : 0;

    //Allow extra length in the x-axis range so that we can display the labels outside
    const maxValue = Math.max(...array) * maxMultiplier;

    const layout = Object.assign({}, defaultLayout, {
        margin: { l: 108, r: 0, b: 0, t: 0, pad: 1 },
        autosize: true,
        xaxis: { visible: false, fixedrange: true, range: [0, maxValue] },
        cliponaxis: false,
        clickmode: "select",
    });
    if (!allowFilter) {
        layout.dragmode = false;
    }

    return {
        maxValue,
        outerStyle: { minHeight: maxHeight, maxHeight: maxHeight, height: maxHeight, overflowY: barsHeight <= maxHeight ? "hidden" : "scroll" } as React.CSSProperties,
        innerStyle: { marginTop: verticalAlign, minHeight: barsHeight, maxHeight: barsHeight, height: barsHeight } as React.CSSProperties,
        layout,
    };
}

/**
 * Chart keys for React
 * @param heading 
 */
export function computeChartKey(heading: string) {
    return `DASHBOARD_${heading.toUpperCase().replace(/\s/g, "_")}`;
}

/**
 * When denominator is not 0, returns the result in the form "#% (#,###)", with optional rounding parameter
 * @param numerator 
 * @param denominator 
 */
 export function percentWithCount(numerator: number, denominator: number, delimiter: string = " ", dp?: number): string {
    if (denominator === 0)
        return "0";

    const percent = (numerator / denominator * 100.0);

    return `${toFixed(percent, dp)}%${delimiter}(${numerator.toLocaleString()})`;
}

/**
 * When denominator is not 0, returns the result in the form "#,### (#%)" 
 * @param numerator 
 * @param denominator 
 */
export function countWithPercent(numerator: number, denominator: number, delimiter: string = " "): string {
    if (denominator === 0)
        return "0";

    const percent = (numerator / denominator * 100.0);
    return `${numerator.toLocaleString()}${delimiter}(${toFixed(percent)}%)`;
}

/**
 * When denominator is not 0, returns the result as a percentage
 * @param numerator 
 * @param denominator 
 */
export function percentValue(numerator: number, denominator: number): number {
    if (denominator === 0)
        return 0;

    return (numerator / denominator * 100.0);
}

/**
 * When denominator is not 0, returns the result in the form "#%" 
 * @param numerator 
 * @param denominator 
 */
export function percent(numerator: number, denominator: number): string {
    const percent = percentValue(numerator, denominator);
    return `${toFixed(percent)}%`;
}

/**
 * When dp is not specified, truncates trailing zeroes from the rounded value (to 2 dp).
 * E.g. 
 *      2.60 returns "2.6"
 *      3.00 returns "3"
 * @param value 
 * @param dp 
 */
export function toFixed(value: number, dp?: number): string {
    if (value === null || value === undefined) {
        return "";
    }

    if (dp !== undefined) {
        return `${value.toFixed(dp)}`;
    }

    let v = value.toFixed(2);
    while (v.endsWith("0")) {
        v = v.substr(0, v.length - 1);
    }
    if (v.endsWith(".")) {
        v = v.substr(0, v.length - 1);
    }
    return v;
}

/**
 * Creates a basic function to apply the filter with the selected values
 * @param filterKey key of the filter to be applied
 * @param filterFunc function to filter
 * @param context path/context information
 */
export function addFilterFactory(addFilter: AddFilterBaseFunc<any>, filterKey: string, pointValueFunc: (point: Plotly.PlotDatum) => string, context?: FilterContext): ChartEventHandler {
    return (e: { points: Plotly.PlotDatum[]; }) => {
        if (!filterKey || !e || !e.points || e.points.length === 0) {
            return;
        }

        const uniques = new Set<string>(e.points.map(p => pointValueFunc(p)));
        addFilter(filterKey, [...uniques].sort(), context);
    };
}

/**
 * Creates a function to apply the filter with the selected values 
 * using the supplied function to extract the value from the view model
 * @param filterKey key of the filter to be applied
 * @param pointValueFunc function to extract the values from the points
 */
export function simpleFilterFactory(addFilter: AddFilterBaseFunc<any>, filterKey: string, pointValueFunc: (point: Plotly.PlotDatum) => string): ChartEventHandler {
    return addFilterFactory(
        addFilter,
        filterKey,
        pointValueFunc
    );
}

/**
 * Returns a ChartEventHandler that adds a filter for a hierarchical chart. 
 * The filter will apply each level of the hierearchy in turn to the view model based on the context against the hierarchy
 * as at the time of function creation.
 * 
 * @param hierarchy The hierarchy to be filtered against
 * @param filterKey The key for the filter being created
 * @param valueFn Function to extract the value from the selected plotly datums.
 */
export function addHierarchyFilterFactory(addFilter: AddFilterBaseFunc<any>, filterKey: string, hierarchy: IHierarchyPlotData<any>, valueFn: (p: Plotly.PlotDatum) => string): ChartEventHandler {
    const { path, filterMore } = hierarchy;
	
    if (!filterMore)
        return null;

    const context = path && FilterContext.fromPath(path) || new FilterContext();
    return addFilterFactory(addFilter, filterKey, valueFn, context);
}

/**
 * Creates a function to apply a filter for a legend element click.
 * @param filterKey key of the filter to be applied
 * @param legendValue the valuie to apply for the legend
 */
export function legendFilterFactory(addFilter: AddFilterBaseFunc<any>, filterKey: string): LegendFilter {
    return legendValue => addFilter(filterKey, legendValue);
}
